import React from 'react'
import PropTypes from 'prop-types'
import { Navigate } from 'react-router-dom'
import { ROLES, hasPermission, ROLE_HIERARCHY } from '@config/roles'
import authService from '@services/authService'

/**
 * Get user's frontend role from auth service
 * Maps backend role slugs to frontend ROLES constants
 */
const getUserRole = () => {
	const user = authService.getCurrentUser()

	if (!user || !user.role) {
		return null
	}

	// Handle both role object (from backend) and role string
	const roleSlug = typeof user.role === 'object'
		? user.role.slug
		: user.role

	// Map backend role slugs to frontend ROLES constants
	const roleMapping = {
		'super-admin': ROLES.SUPER_ADMIN,
		'superadmin': ROLES.SUPER_ADMIN,
		'product-owner': ROLES.SUPER_ADMIN,
		'company-admin': ROLES.COMPANY_ADMIN,
		'compliance-manager': ROLES.COMPANY_ADMIN,
		'project-manager': ROLES.PROJECT_MANAGER,
		'management': ROLES.MANAGEMENT,
		'qa': ROLES.MANAGEMENT,
		'executive': ROLES.MANAGEMENT,
		'employee': ROLES.EMPLOYEE,
		'team-member': ROLES.EMPLOYEE
	}

	return roleMapping[roleSlug?.toLowerCase()] || ROLES.EMPLOYEE
}

/**
 * RoleGuard Component
 *
 * Protects routes based on user role or specific permissions.
 * Redirects to AccessDenied page if user lacks required access.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Protected content to render
 * @param {string[]} props.allowedRoles - Array of roles allowed to access (e.g., [ROLES.SUPER_ADMIN])
 * @param {string} props.requiredPermission - Specific permission required (e.g., 'canManageCompanies')
 * @param {number} props.minRoleLevel - Minimum role hierarchy level required
 * @param {string} props.redirectTo - Custom redirect path (default: '/access-denied')
 *
 * @example
 * // Allow only Super Admin
 * <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN]}>
 *   <SuperAdminPage />
 * </RoleGuard>
 *
 * @example
 * // Allow Super Admin and Company Admin
 * <RoleGuard allowedRoles={[ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN]}>
 *   <AdminPage />
 * </RoleGuard>
 *
 * @example
 * // Require specific permission
 * <RoleGuard requiredPermission="canManageCompanies">
 *   <CompaniesPage />
 * </RoleGuard>
 *
 * @example
 * // Require minimum role level (e.g., Project Manager or higher)
 * <RoleGuard minRoleLevel={3}>
 *   <ManagerPage />
 * </RoleGuard>
 */
const RoleGuard = ({
	children,
	allowedRoles = [],
	requiredPermission = null,
	minRoleLevel = null,
	redirectTo = '/access-denied'
}) => {
	const userRole = getUserRole()

	// If no user role found, redirect to login
	if (!userRole) {
		return <Navigate to="/login" replace />
	}

	let hasAccess = false

	// Check 1: Allowed roles list
	if (allowedRoles.length > 0) {
		hasAccess = allowedRoles.includes(userRole)
	}

	// Check 2: Required permission
	if (!hasAccess && requiredPermission) {
		hasAccess = hasPermission(userRole, requiredPermission)
	}

	// Check 3: Minimum role level
	if (!hasAccess && minRoleLevel !== null) {
		const userLevel = ROLE_HIERARCHY[userRole] || 0
		hasAccess = userLevel >= minRoleLevel
	}

	// If no checks were specified, allow access (shouldn't happen in practice)
	if (allowedRoles.length === 0 && !requiredPermission && minRoleLevel === null) {
		hasAccess = true
	}

	// Redirect if no access
	if (!hasAccess) {
		return <Navigate to={redirectTo} replace />
	}

	// Render protected content
	return children
}

RoleGuard.propTypes = {
	children: PropTypes.node.isRequired,
	allowedRoles: PropTypes.arrayOf(PropTypes.string),
	requiredPermission: PropTypes.string,
	minRoleLevel: PropTypes.number,
	redirectTo: PropTypes.string
}

export default RoleGuard
